import { useState } from 'react';
import { MagnifyingGlassIcon } from '@rsos/assets/icons';
import type { IarAgency } from '@rsos/reduxDataStore/src/features/eventShare/types';
import AgencyAccordion from './AgencyAccordion';
import styles from './IarAgencySelector.module.scss';
import type { IarSelectedMember } from './helpers';

interface IarAgencySelectorProps {
  agencies: IarAgency[];
  selectedMembers: IarSelectedMember[];
  org: string;
  onChange: (members: IarSelectedMember[]) => void;
}

const IarAgencySelector = ({
  agencies,
  selectedMembers,
  org,
  onChange,
}: IarAgencySelectorProps) => {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  const toggleExpand = (agencyId: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(agencyId)) {
        next.delete(agencyId);
      } else {
        next.add(agencyId);
      }
      return next;
    });
  };

  const getAgencyMemberCount = (agencyId: number) =>
    selectedMembers.filter(m => m.agency_id === agencyId).length;

  const handleAgencyToggle = (
    agency: IarAgency,
    visibleSelections: IarSelectedMember[],
  ) => {
    const agencyId = agency.legacyId;
    const visibleIds = new Set(visibleSelections.map(s => s.member_id));
    const currentSelectedCount = selectedMembers.filter(
      m => m.agency_id === agencyId && visibleIds.has(m.member_id),
    ).length;
    const allSelected =
      currentSelectedCount === visibleSelections.length &&
      visibleSelections.length > 0;

    if (allSelected) {
      onChange(
        selectedMembers.filter(
          m => !(m.agency_id === agencyId && visibleIds.has(m.member_id)),
        ),
      );
    } else {
      const existing = new Set(
        selectedMembers
          .filter(m => m.agency_id === agencyId)
          .map(m => m.member_id),
      );
      const newSelections = visibleSelections.filter(
        s => !existing.has(s.member_id),
      );
      onChange([...selectedMembers, ...newSelections]);
    }
  };

  if (agencies.length === 0) {
    return (
      <p className={styles.emptyState}>
        No IamResponding agencies available for your organization.
      </p>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.searchContainer}>
        <div className={styles.searchIconWrapper}>
          <MagnifyingGlassIcon width={14} height={14} />
        </div>
        <input
          type="text"
          className={styles.searchInput}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search members by name or position..."
        />
      </div>
      {agencies.map(agency => (
        <AgencyAccordion
          key={agency.legacyId}
          org={org}
          agency={agency}
          searchTerm={searchTerm}
          selectedMembers={selectedMembers}
          onChange={onChange}
          isExpanded={expandedIds.has(agency.legacyId)}
          onToggleExpand={toggleExpand}
          getAgencyMemberCount={getAgencyMemberCount}
          onAgencyToggle={handleAgencyToggle}
        />
      ))}
    </div>
  );
};

export default IarAgencySelector;
